import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../config';
import { formatDatePSTShort } from '../../utils/dateUtils';
import './BillingDashboard.css';

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  total: number;
  balance_due: number;
  status: string;
  student_id: string;
  student_name?: string;
  parent_name?: string;
}

const toAmount = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

interface InvoiceListProps {
  studentId?: string;
  parentId?: string;
  onInvoiceClick?: (invoiceId: string) => void;
  onPayInvoice?: (invoiceId: string) => void;
  onViewInvoice?: (invoiceId: string) => void;
  onDownloadInvoice?: (invoiceId: string) => void;
  onDeleteInvoice?: (invoiceId: string) => void;
  onStatusChange?: (invoiceId: string, newStatus: string) => void;
  isAdmin?: boolean;
  refreshTrigger?: number; // When this changes, refresh the invoice list
}

const InvoiceList: React.FC<InvoiceListProps> = ({
  studentId,
  parentId,
  onInvoiceClick,
  onPayInvoice,
  onViewInvoice,
  onDownloadInvoice,
  onDeleteInvoice,
  onStatusChange,
  isAdmin,
  refreshTrigger
}) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    dateFrom: '',
    dateTo: ''
  });
  const [sortBy, setSortBy] = useState<'invoice_number' | 'invoice_date' | 'due_date' | 'student_name' | 'parent_name' | 'total' | 'balance_due' | 'status'>('invoice_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [batchAction, setBatchAction] = useState('');
  const [batchProcessing, setBatchProcessing] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, [studentId, parentId, refreshTrigger]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let url = '';
      if (studentId) {
        url = `${config.BACKEND_URL}/api/billing/invoices/student/${studentId}`;
      } else if (parentId) {
        url = `${config.BACKEND_URL}/api/billing/invoices/parent/${parentId}`;
      } else {
        // Admin view - get all invoices
        url = `${config.BACKEND_URL}/api/billing/invoices`;
      }
      
      const response = await axios.get(url, { headers });
      const normalizedInvoices = (response.data || []).map((invoice: any) => ({
        ...invoice,
        total: toAmount(invoice.total),
        balance_due: toAmount(invoice.balance_due)
      }));
      setInvoices(normalizedInvoices);
      setSelectedInvoiceIds([]);
    } catch (err: any) {
      console.error('Error fetching invoices:', err);
      setError(err.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (invoiceId: string) => {
    if (onViewInvoice) {
      onViewInvoice(invoiceId);
    } else if (onInvoiceClick) {
      onInvoiceClick(invoiceId);
    }
  };

  const handlePay = (invoiceId: string) => {
    if (onPayInvoice) {
      onPayInvoice(invoiceId);
    }
  };

  const handleDownload = (invoiceId: string) => {
    if (onDownloadInvoice) {
      onDownloadInvoice(invoiceId);
    }
  };

  const handleDelete = (invoiceId: string) => {
    if (onDeleteInvoice) {
      onDeleteInvoice(invoiceId);
    }
  };

  const getStatusClass = (status: string) => {
    const statusClasses: { [key: string]: string } = {
      paid: 'status-badge status-paid',
      partially_paid: 'status-badge status-partially-paid',
      sent: 'status-badge status-sent',
      overdue: 'status-badge status-overdue',
      draft: 'status-badge status-draft',
      cancelled: 'status-badge status-cancelled'
    };
    return statusClasses[status] || 'status-badge';
  };

  const getStatusLabel = (status: string) => {
    const statusLabels: { [key: string]: string } = {
      paid: 'Paid',
      partially_paid: 'Partially Paid',
      sent: 'Pending',
      overdue: 'Overdue',
      draft: 'Draft',
      cancelled: 'Cancelled'
    };
    return statusLabels[status] || status;
  };

  const handleHeaderSort = (key: typeof sortBy) => {
    if (sortBy === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDirection(key === 'invoice_date' ? 'desc' : 'asc');
    }
  };

  const getSortIndicator = (key: typeof sortBy) => {
    if (sortBy !== key) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (filters.status !== 'all' && invoice.status !== filters.status) {
      return false;
    }
    
    if (filters.search) {
      const searchLower = filters.search.trim().toLowerCase();
      if (
        !(invoice.invoice_number || '').toLowerCase().includes(searchLower) &&
        !(invoice.student_name || '').toLowerCase().includes(searchLower) &&
        !(invoice.parent_name || '').toLowerCase().includes(searchLower)
      ) {
        return false;
      }
    }
    
    if (filters.dateFrom && invoice.invoice_date < filters.dateFrom) {
      return false;
    }
    
    if (filters.dateTo && invoice.invoice_date > filters.dateTo) {
      return false;
    }
    
    return true;
  });

  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'invoice_number':
        comparison = (a.invoice_number || '').localeCompare(b.invoice_number || '');
        break;
      case 'invoice_date':
        comparison = new Date(a.invoice_date).getTime() - new Date(b.invoice_date).getTime();
        break;
      case 'due_date':
        comparison = new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        break;
      case 'student_name':
        comparison = (a.student_name || '').localeCompare(b.student_name || '');
        break;
      case 'parent_name':
        comparison = (a.parent_name || '').localeCompare(b.parent_name || '');
        break;
      case 'total':
        comparison = a.total - b.total;
        break;
      case 'balance_due':
        comparison = a.balance_due - b.balance_due;
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const allSelected = sortedInvoices.length > 0 && sortedInvoices.every(inv => selectedInvoiceIds.includes(inv.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedInvoiceIds([]);
      return;
    }
    setSelectedInvoiceIds(sortedInvoices.map(inv => inv.id));
  };

  const toggleSelectInvoice = (invoiceId: string) => {
    setSelectedInvoiceIds(prev =>
      prev.includes(invoiceId) ? prev.filter(id => id !== invoiceId) : [...prev, invoiceId]
    );
  };

  const runBatchAction = async () => {
    if (!isAdmin || selectedInvoiceIds.length === 0 || !batchAction) return;

    const actionLabelMap: { [key: string]: string } = {
      mark_paid: 'mark selected invoices as Paid',
      mark_pending: 'mark selected invoices as Pending',
      mark_draft: 'mark selected invoices as Draft',
      mark_overdue: 'mark selected invoices as Overdue',
      mark_cancelled: 'mark selected invoices as Cancelled',
      delete: 'permanently delete selected invoices'
    };

    if (!window.confirm(`Are you sure you want to ${actionLabelMap[batchAction]}?`)) {
      return;
    }

    try {
      setBatchProcessing(true);
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (batchAction === 'delete') {
        await Promise.all(
          selectedInvoiceIds.map(invoiceId =>
            axios.post(`${config.BACKEND_URL}/api/billing/invoices/${invoiceId}/hard-delete`, {}, { headers })
          )
        );
      } else {
        const actionToStatus: { [key: string]: string } = {
          mark_paid: 'paid',
          mark_pending: 'sent',
          mark_draft: 'draft',
          mark_overdue: 'overdue',
          mark_cancelled: 'cancelled'
        };
        const newStatus = actionToStatus[batchAction];
        await Promise.all(
          selectedInvoiceIds.map(invoiceId =>
            axios.put(`${config.BACKEND_URL}/api/billing/invoices/${invoiceId}`, { status: newStatus }, { headers })
          )
        );
      }

      setBatchAction('');
      setSelectedInvoiceIds([]);
      await fetchInvoices();
    } catch (err: any) {
      console.error('Error running batch invoice action:', err);
      alert(err.response?.data?.detail || 'Failed to run batch action');
    } finally {
      setBatchProcessing(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading invoices...</div>;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  return (
    <div className="invoice-list-container">
      <div className="invoice-list-filters">
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Invoice number, student, parent..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="filter-input"
          />
        </div>
        
        <div className="filter-group">
          <label>From Date:</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            className="filter-input"
          />
        </div>
        
        <div className="filter-group">
          <label>To Date:</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            className="filter-input"
          />
        </div>
        
        <div className="filter-group">
          <label>Sort:</label>
          <span className="info-value">{sortBy.replace('_', ' ')} ({sortDirection})</span>
        </div>
      </div>
      
      <div className="invoice-list-stats">
        <span>Showing {sortedInvoices.length} of {invoices.length} invoices</span>
        {isAdmin && selectedInvoiceIds.length > 0 && (
          <span style={{ marginLeft: '12px', fontWeight: 600 }}>• {selectedInvoiceIds.length} selected</span>
        )}
      </div>

      {isAdmin && (
        <div className="invoice-batch-actions">
          <select
            value={batchAction}
            onChange={(e) => setBatchAction(e.target.value)}
            className="filter-select"
            disabled={selectedInvoiceIds.length === 0 || batchProcessing}
          >
            <option value="">Batch Actions...</option>
            <option value="mark_paid">Mark as Paid</option>
            <option value="mark_pending">Mark as Pending</option>
            <option value="mark_draft">Mark as Draft</option>
            <option value="mark_overdue">Mark as Overdue</option>
            <option value="mark_cancelled">Mark as Cancelled</option>
            <option value="delete">Delete Permanently</option>
          </select>
          <button
            className="btn btn-primary btn-sm"
            disabled={selectedInvoiceIds.length === 0 || !batchAction || batchProcessing}
            onClick={runBatchAction}
          >
            {batchProcessing ? 'Applying...' : 'Apply'}
          </button>
        </div>
      )}
      
      {sortedInvoices.length === 0 ? (
        <div className="empty-state">
          <p>No invoices found</p>
        </div>
      ) : (
        <div className="invoice-table-wrapper">
          <table className="invoice-table">
            <thead>
              <tr>
                {isAdmin && (
                  <th style={{ width: '42px' }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                  </th>
                )}
                <th onClick={() => handleHeaderSort('invoice_number')} className="sortable-header">
                  Invoice{getSortIndicator('invoice_number')}
                </th>
                <th onClick={() => handleHeaderSort('invoice_date')} className="sortable-header">
                  Date{getSortIndicator('invoice_date')}
                </th>
                <th onClick={() => handleHeaderSort('due_date')} className="sortable-header">
                  Due Date{getSortIndicator('due_date')}
                </th>
                <th onClick={() => handleHeaderSort('student_name')} className="sortable-header">
                  Student{getSortIndicator('student_name')}
                </th>
                <th onClick={() => handleHeaderSort('parent_name')} className="sortable-header">
                  Parent{getSortIndicator('parent_name')}
                </th>
                <th onClick={() => handleHeaderSort('total')} className="sortable-header amount-col">
                  Total{getSortIndicator('total')}
                </th>
                <th onClick={() => handleHeaderSort('balance_due')} className="sortable-header amount-col">
                  Balance Due{getSortIndicator('balance_due')}
                </th>
                <th onClick={() => handleHeaderSort('status')} className="sortable-header">
                  Status{getSortIndicator('status')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedInvoices.map((invoice) => (
                <tr key={invoice.id}>
                  {isAdmin && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedInvoiceIds.includes(invoice.id)}
                        onChange={() => toggleSelectInvoice(invoice.id)}
                      />
                    </td>
                  )}
                  <td>{invoice.invoice_number}</td>
                  <td>{formatDatePSTShort(invoice.invoice_date)}</td>
                  <td>{formatDatePSTShort(invoice.due_date)}</td>
                  <td>{invoice.student_name || '—'}</td>
                  <td>{invoice.parent_name || '—'}</td>
                  <td className="amount-col">${toAmount(invoice.total).toFixed(2)}</td>
                  <td className="amount-col">${toAmount(invoice.balance_due).toFixed(2)}</td>
                  <td>
                    <span className={getStatusClass(invoice.status)}>{getStatusLabel(invoice.status)}</span>
                  </td>
                  <td>
                    <div className="invoice-table-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => handleView(invoice.id)}>View</button>
                      {onDownloadInvoice && (
                        <button className="btn btn-secondary btn-sm" onClick={() => handleDownload(invoice.id)}>PDF</button>
                      )}
                      {toAmount(invoice.balance_due) > 0 && onPayInvoice && (
                        <button className="btn btn-primary btn-sm" onClick={() => handlePay(invoice.id)}>Pay</button>
                      )}
                      {isAdmin && onDeleteInvoice && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(invoice.id)}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;

